import { useMemo, useState, type ChangeEvent } from 'react';
import { Card } from '@/components/ui/card';
import { PoweredByIntralys } from '@/components/shared/PoweredByIntralys';
import { ScrollReveal } from '@/components/ui/scroll-reveal';
import { useLang } from '@/lib/LanguageContext';
import { useLeadModal } from '@/lib/LeadModalContext';
import { ta } from '@/lib/i18n';
import { t } from '@/lib/translations';
import { recommendCapital, type CalcInputs } from '@/lib/calc/amg-recommendation';

/**
 * CalculatriceAMG — modèle « besoins » aligné sur le guide (Partie II).
 *
 * 4 champs (dettes + charges mensuelles + mois de répit + revenu net qui s'arrêterait)
 * → estimation live (dettes + charges × mois) + breakdown + raisonnement.
 * Pas d'email capture. CTA primaire → formulaire (audit).
 *
 * a11y : aria-live sur le bloc résultat, labels associés aux inputs.
 * Mobile : stack form/result vertical sous md.
 */

const DEFAULT_INPUTS: CalcInputs = {
  dettes: 250000,
  chargesMensuelles: 4000,
  moisRepit: 12,
  revenuNet: 3500
};

const FIELDS: ReadonlyArray<{
  key: keyof CalcInputs;
  labelKey: 'q_dettes_label' | 'q_charges_label' | 'q_mois_label' | 'q_revenu_label';
  helpKey: 'q_dettes_help' | 'q_charges_help' | 'q_mois_help' | 'q_revenu_help';
  prefix?: string;
  suffix?: string;
  step: number;
  max?: number;
}> = [
  { key: 'dettes', labelKey: 'q_dettes_label', helpKey: 'q_dettes_help', prefix: '$', step: 5000 },
  { key: 'chargesMensuelles', labelKey: 'q_charges_label', helpKey: 'q_charges_help', prefix: '$', suffix: '/mois', step: 100 },
  { key: 'moisRepit', labelKey: 'q_mois_label', helpKey: 'q_mois_help', suffix: 'mois', step: 1, max: 60 },
  { key: 'revenuNet', labelKey: 'q_revenu_label', helpKey: 'q_revenu_help', prefix: '$', suffix: '/mois', step: 100 }
];

function formatAmount(n: number, lang: 'fr' | 'en'): string {
  // n en dollars absolus.
  return n.toLocaleString(lang === 'fr' ? 'fr-CA' : 'en-CA');
}

export function CalculatriceAMG() {
  const { lang } = useLang();
  const { openForm } = useLeadModal();
  const text = ta(t.calc, lang);

  const [inputs, setInputs] = useState<CalcInputs>(DEFAULT_INPUTS);

  const result = useMemo(() => recommendCapital(inputs), [inputs]);

  const handleNum = (key: keyof CalcInputs) => (e: ChangeEvent<HTMLInputElement>) => {
    const v = parseFloat(e.target.value);
    setInputs((prev) => ({ ...prev, [key]: Number.isNaN(v) ? 0 : Math.max(0, v) }));
  };

  const formattedAmount = formatAmount(result.recommended, lang);
  const reasoning = lang === 'fr' ? result.reasoning_fr : result.reasoning_en;

  return (
    <section id="calculatrice" className="section-cv-auto atmosphere-linen surface-peach-soft relative w-full overflow-hidden py-16 md:py-24">
      <div className="relative z-[1] mx-auto max-w-[1440px] px-5 md:px-20">
        {/* Header */}
        <div className="mb-12 text-center md:mb-16">
          <ScrollReveal as="span" className="eyebrow-pill mb-6">
            {text.eyebrow}
          </ScrollReveal>
          <ScrollReveal
            delay={100}
            as="h2"
            className="mb-6 font-display text-3xl leading-[1.2] text-balance text-foreground md:text-5xl"
          >
            {text.title}
          </ScrollReveal>
          <ScrollReveal
            delay={200}
            as="p"
            className="mx-auto max-w-2xl font-sans text-base text-pretty hyphens-auto text-muted-foreground leading-relaxed"
          >
            {text.intro}
          </ScrollReveal>
          <div className="mx-auto mt-8 h-px w-16 bg-rose-soft" aria-hidden="true" />
        </div>

        {/* Two-column form + result */}
        <div className="grid grid-cols-1 gap-8 md:grid-cols-2 md:gap-10">
          {/* LEFT: Form — 4 chiffres */}
          <ScrollReveal delay={300}>
            <Card className="space-y-7">
              {FIELDS.map((f) => (
                <div key={f.key}>
                  <label
                    htmlFor={`calc-${f.key}`}
                    className="mb-2 block font-sans text-sm font-bold uppercase tracking-widest text-foreground"
                  >
                    {text[f.labelKey]}
                  </label>
                  <div className="flex items-baseline gap-2 border-b-2 border-foreground/30 px-1 py-2 transition-colors focus-within:border-primary">
                    {f.prefix && <span className="font-display text-xl text-muted-foreground">{f.prefix}</span>}
                    <input
                      id={`calc-${f.key}`}
                      name={f.key}
                      type="number"
                      inputMode="numeric"
                      min={0}
                      max={f.max}
                      step={f.step}
                      value={inputs[f.key] === 0 ? '' : inputs[f.key]}
                      onChange={handleNum(f.key)}
                      placeholder="0"
                      className="w-full bg-transparent border-0 p-0 font-display text-2xl text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-0"
                    />
                    {f.suffix && <span className="shrink-0 font-sans text-sm text-muted-foreground">{f.suffix}</span>}
                  </div>
                  <p className="mt-2 font-sans text-sm text-foreground/70">{text[f.helpKey]}</p>
                </div>
              ))}
            </Card>
          </ScrollReveal>

          {/* RIGHT: Result */}
          <ScrollReveal delay={400}>
            <Card
              className="card-warm border-beam space-y-6 transition-colors duration-300 hover:bg-rose-blush/12"
              role="region"
              aria-live="polite"
              aria-label={text.result_label}
            >
              {/* Big number */}
              <div>
                <p className="mb-2 font-sans text-xs font-bold uppercase tracking-widest text-secondary">
                  {text.result_label}
                </p>
                <p className="sheen-once font-display text-5xl leading-tight text-primary md:text-6xl break-words tabular-nums">
                  {formattedAmount}
                  {text.result_suffix}
                </p>
                <p className="mt-3 font-sans text-sm italic text-foreground/80">{reasoning}</p>
                <p className="mt-2 font-sans text-xs leading-relaxed text-pretty hyphens-auto text-muted-foreground">
                  {text.result_note}
                </p>
              </div>

              {/* Breakdown */}
              <div className="border-t border-border/40 pt-4">
                <p className="mb-3 font-sans text-xs font-bold uppercase tracking-widest text-foreground">
                  {text.breakdown_title}
                </p>
                <ul className="space-y-2">
                  {result.breakdown
                    .filter((item) => item.amount > 0)
                    .map((item) => {
                      const label = lang === 'fr' ? item.label_fr : item.label_en;
                      const amountStr =
                        lang === 'fr'
                          ? `${formatAmount(item.amount, 'fr')} $`
                          : `${formatAmount(item.amount, 'en')} CAD`;
                      return (
                        <li
                          key={item.label_fr}
                          className="flex items-baseline justify-between gap-3 font-sans text-sm"
                        >
                          <span className="text-foreground/80">{label}</span>
                          <span className="shrink-0 font-display text-base text-foreground">{amountStr}</span>
                        </li>
                      );
                    })}
                </ul>
              </div>

              {/* Disclaimer */}
              <p className="border-t border-border/40 pt-4 font-sans text-xs leading-relaxed text-pretty hyphens-auto text-muted-foreground">
                {text.disclaimer}
              </p>

              {/* CTAs */}
              <div className="flex flex-col gap-3 pt-2 sm:flex-row">
                <button
                  type="button"
                  onClick={openForm}
                  data-cursor="cta"
                  className="shadow-warm-soft hover:shadow-warm-md inline-flex flex-1 items-center justify-center rounded-md bg-primary px-6 py-4 text-center font-sans text-xs font-bold uppercase tracking-widest text-primary-foreground transition-all hover:bg-primary/90"
                >
                  {text.cta_audit}
                </button>
                <a
                  href="/#methode"
                  className="inline-flex flex-1 items-center justify-center rounded-md border border-border bg-background px-6 py-4 text-center font-sans text-xs font-bold uppercase tracking-widest text-foreground transition-colors hover:bg-muted"
                >
                  {text.cta_method}
                </a>
              </div>
            </Card>
          </ScrollReveal>
        </div>

        <PoweredByIntralys />
      </div>
    </section>
  );
}
